import { DateTime } from "luxon";
import storageKey from "./storageKey";
import { CLG } from "./util/consoleUtil";

type RetrieveAllStoreType = (
  storeName: AvailableStoresType,
  limit?: number
) => Promise<DataBaseObjectType[]>;

type SaveDataToStoreType = (
  storeName: AvailableStoresType,
  dataContent: ContentObjectType
) => Promise<void>;

export type ContentObjectType = {
  id?: number;
  content: string | object | File;
  createdAtTimestamp?: number;
};

export type DataBaseObjectType = { key: string; value: ContentObjectType };

type AvailableStoresType = "app-log" | "file-objects";

/**
 * Based on: https://codepen.io/MSEdgeDev/pen/YqqOLd
 *
 * Alternatively can use Dexie available at https://dexie.org/docs/Tutorial/React
 * Wich is a abstraction os IndexDB
 */
export class MainIndexDB {
  private static isAvailable = "indexedDB" in window;

  private static DBObject: any = null;

  private static ObjectsStoredLimit = 10;

  private static DBName = "circular-app-storage";
  private static DBVersion = 1;
  private static DBStoresAvailable: AvailableStoresType[] = ["app-log", "file-objects"];

  private static isDBOpened = false;

  //

  public static loadedData = { "app-log": [], "file-objects": [] };

  //

  private static requiredFeaturesSupported = () => {
    const availableProtocols = ["http:", "https:", "ms-wwa-web:", "ms-wwa:"];

    if (availableProtocols.includes(window.location.protocol)) {
      return true;
    }

    console.log(...CLG.Error("requiredFeaturesSupported: not supported"));
    return false;
  };

  private static openDB = async () => {
    return new Promise(async (Ok, Rej) => {
      if (!MainIndexDB.isAvailable) {
        console.log(...CLG.Error("openDB: [Not Available]"));
        return Rej();
      }

      try {
        var openRequest = window.indexedDB.open(MainIndexDB.DBName, MainIndexDB.DBVersion);

        openRequest.onerror = (evt: any) => {
          console.log(
            ...CLG.Error("openRequest.onerror:"),
            evt.target.error ? evt.target.error : evt.target.errorCode
          );

          return Rej();
        };

        openRequest.onblocked = (evt: any) => {
          console.log(
            ...CLG.Error("openRequest.onblocked:"),
            evt.target.error ? evt.target.error : evt.target.errorCode
          );

          return Rej();
        };

        openRequest.onupgradeneeded = async (evt: any) => {
          var db = (MainIndexDB.DBObject = evt.target.result);

          if (!db) {
            console.log(...CLG.Error("openRequest.onupgradeneeded, [database is Null]:"));
            return Rej();
          }

          try {
            await Promise.all(
              MainIndexDB.DBStoresAvailable.map((storeName) =>
                db.createObjectStore(storeName, {
                  keyPath: "id",
                  autoIncrement: true,
                })
              )
            );
          } catch (err) {
            console.log(...CLG.Error("openRequest.onupgradeneeded, createObjectStore:"), err);
            return Rej();
          }

          console.log(...CLG.Success("openRequest.onupgradeneeded: [Database Updated]"));
        };

        openRequest.onsuccess = (evt: any) => {
          var db = (MainIndexDB.DBObject = evt.target.result);

          if (!db) {
            console.log(...CLG.Error("openRequest.onsuccess, [database is Null]:"));
            return Rej();
          }

          console.log(...CLG.Success("openRequest.onsuccess: [Database Opened]"));
          MainIndexDB.isDBOpened = true;
          Ok(true);
        };
      } catch (err) {
        console.log(...CLG.Error("openDB:"), err);
        return Rej();
      }
    });
  };

  private static handlePreventOverflow = async () => {
    try {
      const lastOverFlow = window.localStorage.getItem(storageKey.LAST_CLEAR_INDEXDB);
      if (lastOverFlow && lastOverFlow > DateTime.now().minus({ days: 3 }).toFormat("yLLdd")) {
        return;
      }

      if (!MainIndexDB.isDBOpened) await MainIndexDB.openDB();

      MainIndexDB.DBStoresAvailable.forEach(async (storeName) => {
        var transaction = await MainIndexDB.DBObject.transaction(
          storeName,
          "READ_ONLY" in IDBTransaction ? IDBTransaction.READ_ONLY : "readonly"
        );

        var objectStore = transaction.objectStore(storeName);

        try {
          const countRequest = objectStore.count();

          countRequest.onerror = (evt: any) => {
            console.log(
              ...CLG.Error("countRequest.onerror:"),
              evt.target.error ? evt.target.error : evt.target.errorCode
            );
          };

          countRequest.onsuccess = async (evt: any) => {
            const totalCount = evt.target.result;
            if (totalCount > MainIndexDB.ObjectsStoredLimit) {
              const listOfObjects = await MainIndexDB.retrieveAllStore(
                storeName,
                MainIndexDB.ObjectsStoredLimit
              );
              const oldestObject = listOfObjects.pop();

              console.log("OLD", oldestObject);

              var transaction = await MainIndexDB.DBObject.transaction(
                storeName,
                "READWRITE" in IDBTransaction ? IDBTransaction.READWRITE : "readwrite"
              );
              var objectStore = transaction.objectStore(storeName);
              let deleteRequest = objectStore.delete(
                IDBKeyRange.upperBound(oldestObject?.key, true)
              );

              deleteRequest.onerror = (evt: any) => {
                console.log(
                  ...CLG.Error("deleteRequest.onerror:"),
                  evt.target.error ? evt.target.error : evt.target.errorCode
                );
              };

              deleteRequest.oncomplete = (evt: any) => {
                console.log(...CLG.Success("deleteRequest, [Data Deleted]"));
              };

              window.localStorage.setItem(
                storageKey.LAST_CLEAR_INDEXDB,
                DateTime.now().toFormat("yLLdd")
              );
            }
          };
        } catch (innerErr) {
          console.log(...CLG.Error("handlePreventOverflow, innerErr:"), innerErr);
        }
      });
    } catch (err) {
      console.log(...CLG.Error("handlePreventOverflow, transaction:"), err);
    } finally {
      return;
    }
  };

  //

  public static retrieveAllStore: RetrieveAllStoreType = async (storeName, limit = 20) => {
    return new Promise(async (Ok, Rej) => {
      try {
        if (!MainIndexDB.isDBOpened) await MainIndexDB.openDB();

        try {
          var transaction = await MainIndexDB.DBObject.transaction(
            storeName,
            "READ_ONLY" in IDBTransaction ? IDBTransaction.READ_ONLY : "readonly"
          );
        } catch (err) {
          console.log(...CLG.Error("retrieveAllStore, transaction:"), err);
          return Rej();
        }

        console.log(...CLG.Success("retrieveAllStore: [Transaction Created]"));

        try {
          MainIndexDB.loadedData = {
            ...MainIndexDB.loadedData,
            [storeName]: [],
          };

          var objectStore = transaction.objectStore(storeName);

          const filterKey = "id";
          const filterOrder: "next" | "prev" = "prev";

          try {
            var cursorRequest = objectStore.openCursor(null, filterOrder);

            cursorRequest.onerror = (evt: any) => {
              console.log(
                ...CLG.Error("cursorRequest.onerror:"),
                evt.target.error ? evt.target.error : evt.target.errorCode
              );
              return Rej();
            };

            const newState: DataBaseObjectType[] = [];
            let counter = 0;

            cursorRequest.onsuccess = (evt: any) => {
              var cursor = evt.target.result;

              if (cursor && counter < limit) {
                newState.push({ key: cursor.key, value: cursor.value });
                counter++;
                cursor.continue();
              } else {
                console.log(
                  ...CLG.Success("retrieveAllStore, cursorRequest.onsuccess: [Data Retrieved]"),
                  newState
                );
                MainIndexDB.loadedData = {
                  ...MainIndexDB.loadedData,
                  [storeName]: newState,
                };
                Ok(newState);
              }
            };
          } catch (innerErr) {
            console.log(...CLG.Error("retrieveAllStore, innerErr:"), innerErr);
            return Rej();
          }
        } catch (outerErr) {
          console.log(...CLG.Error("retrieveAllStore, outerErr:"), outerErr);
          return Rej();
        }
      } catch (error) {
        return Rej();
      }
    });
  };

  public static saveDataToStore: SaveDataToStoreType = async (storeName, dataContent) => {
    await MainIndexDB.handlePreventOverflow();

    if (!MainIndexDB.isDBOpened) await MainIndexDB.openDB();

    try {
      var transaction = await MainIndexDB.DBObject.transaction(
        storeName,
        "READ_WRITE" in IDBTransaction ? IDBTransaction.READ_WRITE : "readwrite"
      );
    } catch (err) {
      console.log(...CLG.Error("saveDataToStore, transaction error:"), err);
      return;
    }

    transaction.onerror = (evt: any) => {
      console.log(
        ...CLG.Error("transaction.onerror:"),
        evt.target.error ? evt.target.error : evt.target.errorCode
      );
    };

    transaction.onabort = () => {
      console.log(...CLG.Error("transaction.onabort"));
    };

    transaction.oncomplete = () => {
      console.log(...CLG.Success("transaction.oncomplete"));
    };

    try {
      // Note that multiple put()'s can occur per transaction.
      var objectStore = transaction.objectStore(storeName);

      // The put() method will update an existing record, whereas the add() method won't.
      var putRequest = objectStore.put(dataContent);

      // There's at least one object in the database's object store.
      putRequest.onsuccess = () => {
        console.log(...CLG.Success("putRequest.onsuccess, [Data Saved]"));
        MainIndexDB.retrieveAllStore(storeName);
      };

      putRequest.onerror = (evt: any) => {
        console.log(
          ...CLG.Error("putRequest.onerror, [Error Saving Data]:"),
          evt.target.error ? evt.target.error : evt.target.errorCode
        );
      };
    } catch (err) {
      console.log(...CLG.Error("saveDataToStore, put:"), err);
      return;
    }
  };

  //

  public static deleteDB = () => {
    try {
      // If the database is open, you must first close the database connection before deleting it.
      // Otherwise, the delete request waits (possibly forever) for the required close request to occur.
      if (MainIndexDB.DBObject) MainIndexDB.DBObject.close();

      var deleteRequest = window.indexedDB.deleteDatabase(MainIndexDB.DBName);

      deleteRequest.onerror = (evt: any) => {
        console.log(
          ...CLG.Error("deleteRequest.onerror:"),
          evt.target.error ? evt.target.error : evt.target.errorCode
        );
      };

      deleteRequest.onsuccess = () => {
        MainIndexDB.DBObject = null;
        console.log(...CLG.Success("deleteDBRequest"));
      };
    } catch (err) {
      console.log(...CLG.Error("deleteDB:"), err);
    }
  };

  /**
   * Get all stores, check if the keyValue passed is included on the content[key] and delete it;
   */
  public static deleteItemsDB = async (
    storeName: AvailableStoresType,
    filtering: {
      key: string;
      value: string | number;
      comparison: "includes" | ">=" | ">" | "==" | "<=" | "<";
    }[]
  ) => {
    try {
      const allItems = await this.retrieveAllStore(storeName);

      const filteredItems = allItems.filter((item) => {
        const filterResult = filtering.map((filter) => {
          const contentAsString = String((item.value.content as any)[filter.key]);
          switch (filter.comparison) {
            case "includes":
              return contentAsString.includes(String(filter.value));
            case "<":
              return contentAsString < String(filter.value);
            case "<=":
              return contentAsString <= String(filter.value);
            case "==":
              return contentAsString == String(filter.value);
            case ">":
              return contentAsString > String(filter.value);
            case ">=":
              return contentAsString >= String(filter.value);
            default:
              return false;
          }
        });
        return !filterResult.some((filterResult) => !filterResult);
      });

      var transaction = await MainIndexDB.DBObject.transaction(
        storeName,
        "READWRITE" in IDBTransaction ? IDBTransaction.READWRITE : "readwrite"
      );

      const listOfRequests = await Promise.all<IDBOpenDBRequest>(
        filteredItems.map((item) => {
          return transaction.objectStore(storeName).delete(item.key);
        })
      );

      listOfRequests.map((deleteRequest) => {
        deleteRequest.onerror = (evt: any) => {
          console.log(
            ...CLG.Error("deleteRequest.onerror:"),
            evt.target.error ? evt.target.error : evt.target.errorCode
          );
        };

        deleteRequest.onsuccess = () => {
          console.log(...CLG.Success("deleteItemRequest"));
        };
      });
    } catch (err) {
      console.log(...CLG.Error("deleteDB:"), err);
    }
  };
}
